import { useState, useEffect } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

const REGIONS = ['Fort Worth', 'Dallas', 'Denton'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function DriverManagementPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDriver, setEditDriver] = useState(null);
  const [error, setError] = useState('');

  async function load() {
    try { setDrivers(await api.getDrivers()); }
    catch (err) { setError(err.message); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Driver Management</h2>
        <p className="text-muted">Configure driver shifts, regions, and work days. Create driver accounts in User Management first.</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Driver</th><th>Shift</th><th>Regions</th><th>Work Days</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(d => {
              const regions = d.regions ? (typeof d.regions === 'string' ? JSON.parse(d.regions) : d.regions) : [];
              const workDays = d.work_days ? (typeof d.work_days === 'string' ? JSON.parse(d.work_days) : d.work_days) : {};
              const activeDays = DAYS.filter(day => workDays[day]);
              return (
                <tr key={d.id}>
                  <td><strong>{d.full_name}</strong><br /><span className="text-muted">{d.username}</span></td>
                  <td><span className={`shift-badge shift-${d.shift_type || 'day'}`}>{d.shift_type === 'night' ? 'Night' : 'Day'}</span></td>
                  <td>{regions.length > 0 ? regions.join(', ') : <span className="text-muted">None</span>}</td>
                  <td>{activeDays.length > 0 ? activeDays.map(d => d.slice(0,3)).join(', ') : <span className="text-muted">None</span>}</td>
                  <td><button className="btn btn-sm btn-primary" onClick={() => setEditDriver(d)}>Configure</button></td>
                </tr>
              );
            })}
            {drivers.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted">No drivers found. Create driver accounts in User Management first.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editDriver && (
        <DriverProfileModal
          driver={editDriver}
          onClose={() => setEditDriver(null)}
          onSaved={() => { setEditDriver(null); load(); }}
        />
      )}
    </div>
  );
}

function DriverProfileModal({ driver, onClose, onSaved }) {
  const existingRegions = driver.regions ? (typeof driver.regions === 'string' ? JSON.parse(driver.regions) : driver.regions) : [];
  const existingDays = driver.work_days ? (typeof driver.work_days === 'string' ? JSON.parse(driver.work_days) : driver.work_days) : {};

  const [shiftType, setShiftType] = useState(driver.shift_type || 'day');
  const [regions, setRegions] = useState(existingRegions);
  const [workDays, setWorkDays] = useState(existingDays);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function toggleRegion(r) {
    setRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  }

  function toggleDay(day) {
    setWorkDays(prev => ({ ...prev, [day]: !prev[day] }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.updateDriverProfile(driver.id, {
        shift_type: shiftType,
        regions: regions,
        work_days: workDays,
      });
      onSaved();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  }

  return (
    <Modal title={`Configure: ${driver.full_name}`} onClose={onClose}>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Shift Type</label>
          <div className="radio-group">
            <label><input type="radio" value="day" checked={shiftType==='day'} onChange={e=>setShiftType(e.target.value)} /> Day (7AM - 7PM)</label>
            <label><input type="radio" value="night" checked={shiftType==='night'} onChange={e=>setShiftType(e.target.value)} /> Night (7PM - 7AM)</label>
          </div>
        </div>

        <div className="form-group">
          <label>Regions</label>
          <div className="checkbox-group">
            {REGIONS.map(r => (
              <label key={r}>
                <input type="checkbox" checked={regions.includes(r)} onChange={() => toggleRegion(r)} />
                {r}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Work Days</label>
          <p className="form-hint" style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>
            {shiftType === 'night'
              ? 'Night shift: checking a day means that night — Tuesday = Tuesday 7 PM through Wednesday 7 AM.'
              : 'Day shift: checking a day means that day, 7 AM to 7 PM.'}
          </p>
          <div className="checkbox-group work-days-grid">
            {DAYS.map(day => (
              <label key={day} className="work-day-check">
                <input type="checkbox" checked={!!workDays[day]} onChange={() => toggleDay(day)} />
                {day}
              </label>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-muted" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
